import React, {
  useRef,
  useEffect,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Container, Sprite } from "@pixi/react";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import BackgroundAnimationController from "./BackgroundAnimationController";
import * as PIXI from "pixi.js";
import { ASSET_NAME } from "../../../assets";
import { useSelector } from "react-redux";
import {
  selectHoldAndWinActive,
  selectIntroPopupActive,
} from "../../../State/selectors";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";

const BackgroundAnimation = forwardRef((props, ref) => {
  const backgroundControllerRef = useRef(null);
  const backgroundContainerRef = useRef(null);
  const backgroundSpriteRef = useRef(null);
  const { dimensions, symbolWidth, symbolScale, device, orientation } =
    useDimensions();
  const { backgroundAnimationControllerRef } = useReelRefs();

  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const introPopupActive = useSelector(selectIntroPopupActive);

  // Select texture based on Hold and Win active state
  const texture = useMemo(() => {
    return PIXI.Assets.get(
      holdAndWinActive
        ? ASSET_NAME.background_image_feature
        : ASSET_NAME.background_image
    );
  }, [holdAndWinActive]);

  // Initialize BackgroundAnimationController when assets are ready
  useEffect(() => {
    if (
      backgroundContainerRef.current &&
      backgroundSpriteRef.current &&
      !backgroundControllerRef.current
    ) {
      backgroundControllerRef.current = new BackgroundAnimationController(
        backgroundContainerRef.current,
        backgroundSpriteRef.current,
        dimensions,
        symbolWidth,
        symbolScale,
        device,
        orientation,
        introPopupActive
      );

      // Store controller in context ref for access from other components
      backgroundAnimationControllerRef.current =
        backgroundControllerRef.current;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Expose controller methods via ref
  useImperativeHandle(
    ref,
    () => ({
      getController: () => backgroundControllerRef.current,
      playBaseIdle: () => backgroundControllerRef.current?.playBaseIdle(),
      playFeatureIdle: () => backgroundControllerRef.current?.playFeatureIdle(),
      playBaseEventIn: () => backgroundControllerRef.current?.playBaseEventIn(),
      playBaseEventOut: () => backgroundControllerRef.current?.playBaseEventOut(),
    }),
    []
  );

  // Handle dimension changes for responsive design
  useEffect(() => {
    if (backgroundControllerRef.current) {
      backgroundControllerRef.current.resizeBackground(
        dimensions,
        symbolWidth,
        symbolScale,
        device,
        orientation,
        introPopupActive
      );
    }
  }, [
    dimensions,
    symbolWidth,
    symbolScale,
    device,
    orientation,
    introPopupActive,
  ]);

  // Update offset when intro popup state changes
  useEffect(() => {
    if (backgroundControllerRef.current) {
      backgroundControllerRef.current.introPopupActive = introPopupActive;
      backgroundControllerRef.current.updateYOffset();
      backgroundControllerRef.current.applyResponsiveBackgroundScaling();
    }
  }, [introPopupActive]);

  // Update texture when Hold and Win state changes
  useEffect(() => {
    if (backgroundSpriteRef.current && texture) {
      backgroundSpriteRef.current.texture = texture;
    }
  }, [texture]);

  // Play appropriate idle animation based on Hold and Win state
  useEffect(() => {
    if (!backgroundControllerRef.current) {
      return;
    }

    // Don't play any animation if intro popup is active
    if (introPopupActive) {
      return;
    }

    // Clear ALL tracks first to prevent multiple animations playing
    if (backgroundControllerRef.current.backgroundSpine?.state) {
      backgroundControllerRef.current.backgroundSpine.state.clearTracks();
    }

    if (holdAndWinActive) {
      // Play feature_idle when Hold and Win is active
      backgroundControllerRef.current.playFeatureIdle();
    } else {
      // Play base_idle for base game
      backgroundControllerRef.current.playBaseIdle();
    }
  }, [holdAndWinActive, introPopupActive]);

  return (
    <Container ref={backgroundContainerRef} name="background">
      <Sprite
        ref={backgroundSpriteRef}
        name="background_image"
        texture={texture}
        zIndex={0}
        anchor={0.5}
      />
    </Container>
  );
});

BackgroundAnimation.displayName = "BackgroundAnimation";

export default BackgroundAnimation;
